import type { AnyAction } from "redux";
import type { BeforeErrorHook } from "ky";
import { isHTTPError } from "ky";
import type { ThunkAction } from "redux-thunk";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import store, { type RootState } from "@/redux/store";

import type { ApiContext } from "../../types";

import type { ServerErrorPayload } from "./errorShape";

// addToast is untyped JS — cast to make fields optional as they are in practice
type AddToastParams = {
  dismissTimeout?: number;
  hideReportButton?: boolean;
  message?: string;
  messageType?: string;
  requestId?: string | null;
};
// addToast is a thunk (dispatches ADD_TOAST internally), not a plain action creator
const typedAddToast = addToast as unknown as (
  params: AddToastParams,
) => ThunkAction<void, RootState, undefined, AnyAction>;

const dispatch = store.dispatch;

// Intentionally excluded from base.ts and public.ts — the unauthenticated http instance
// rejects with errorPayload only (no toast), matching legacy axios.js behavior for `http`.
// All authenticated instances (auth, background, workflow, calendar) include this hook.
export const errorToastHook: BeforeErrorHook = ({ error: httpError, options }) => {
  if (!isHTTPError(httpError)) return httpError;

  const status = httpError.response.status;
  const isNonAuthFailure = status !== 401 && status !== 403;

  if (!isNonAuthFailure) return httpError;

  // errorPayload is set by errorShapeHook (runs first on base)
  const errorPayload =
    (httpError as typeof httpError & { errorPayload?: ServerErrorPayload }).errorPayload ?? {};
  const { message, request_id: requestId, type } = errorPayload;

  const context = (options.context as ApiContext | undefined) ?? {};
  const {
    conditionalExceptionReporting,
    exceptionConfig,
    preventAllErrorToast,
    toastErrorType: defaultToastErrorType,
  } = context;

  const config = exceptionConfig?.[type ?? ""];
  const preventToast = preventAllErrorToast || config?.preventToast;
  const toastDismissTimeout = config?.toastDismissTimeout ?? 10000;
  const toastErrorType = config?.toastErrorType ?? defaultToastErrorType;

  if (!preventToast) {
    if (toastErrorType) {
      dispatch(
        typedAddToast({
          dismissTimeout: toastDismissTimeout,
          message,
          messageType: toastErrorType,
        }),
      );
    } else {
      // Conditionally include requestId — for 5xx show it, for 4xx hide it when
      // conditionalExceptionReporting is set
      const optionalRequestId = conditionalExceptionReporting
        ? status >= 500
          ? requestId
          : null
        : requestId;

      dispatch(
        typedAddToast({
          dismissTimeout: toastDismissTimeout,
          hideReportButton: optionalRequestId === undefined,
          message,
          messageType: toastMessageTypes.error_v2,
          requestId: optionalRequestId,
        }),
      );
    }
  }

  config?.errorCallback?.(errorPayload);

  return httpError;
};
